import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { http, money, formatApiError, mesi, statusColors, API } from "@/lib/api";
import { COST_CATEGORIES, BASE_OPTIONS, normalizeBase } from "@/lib/reportCalc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ChevronLeft, FileText, Plus, Trash2, Copy, RefreshCw, Lock, Eye } from "lucide-react";
import { toast } from "sonner";
import ReportPreview from "@/components/ReportPreview";
import { ReportSettlement } from "@/components/ReportSettlement";

const PETROL = "#2E5C60";

function Flag({ checked, onChange, label, testid }) {
  return (
    <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
      {label}
    </label>
  );
}

function DeductionRow({ label, amountKey, form, upd, testidPrefix }) {
  return (
    <div className="rounded-xl border border-slate-200 p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-sm">{label}</Label>
        <Input type="number" step="0.01" className="w-32 text-right" value={form[amountKey] ?? 0}
          onChange={(e) => upd(amountKey, e.target.value)} data-testid={`${testidPrefix}-amount`} />
      </div>
      <div className="flex gap-4">
        <Flag checked={form[`${amountKey}_considera`] !== false} onChange={(v) => upd(`${amountKey}_considera`, v)} label="Considera nel calcolo" testid={`${testidPrefix}-considera`} />
        <Flag checked={form[`${amountKey}_show`] !== false} onChange={(v) => upd(`${amountKey}_show`, v)} label="Mostra nel PDF" testid={`${testidPrefix}-show`} />
      </div>
    </div>
  );
}

function PlatformCard({ prefix, name, form, upd, live }) {
  const p = live[prefix];
  const mode = form[`${prefix}_mode`] || "lordo";
  return (
    <Card className="p-5 rounded-2xl space-y-3" data-testid={`${prefix}-card`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="font-display text-lg font-bold" style={{ color: PETROL }}>{name}</div>
        <div className="flex rounded-full border border-slate-200 overflow-hidden text-xs font-semibold">
          {["lordo", "netto"].map((m) => (
            <button key={m} type="button" data-testid={`${prefix}-mode-${m}`} onClick={() => upd(`${prefix}_mode`, m)}
              className={`px-4 py-2 min-h-[36px] ${mode === m ? "text-white" : "text-slate-600 bg-white"}`}
              style={mode === m ? { background: PETROL } : {}}>{m === "lordo" ? "Lordo" : "Netto"}</button>
          ))}
        </div>
      </div>

      {mode === "lordo" ? (
        <>
          <div><Label>Lordo (€)</Label><Input type="number" step="0.01" value={form[`${prefix}_gross`] ?? 0} onChange={(e) => upd(`${prefix}_gross`, e.target.value)} data-testid={`${prefix}-gross`} /></div>
          <DeductionRow label="Commissioni OTA" amountKey={`${prefix}_commissions`} form={form} upd={upd} testidPrefix={`${prefix}-comm`} />
          <DeductionRow label="Ritenuta fiscale (facoltativa)" amountKey={`${prefix}_withholding`} form={form} upd={upd} testidPrefix={`${prefix}-rit`} />
          <DeductionRow label="Altre trattenute" amountKey={`${prefix}_other`} form={form} upd={upd} testidPrefix={`${prefix}-other`} />
        </>
      ) : (
        <div><Label>{name} netto (€)</Label><Input type="number" step="0.01" value={form[`${prefix}_net_input`] ?? 0} onChange={(e) => upd(`${prefix}_net_input`, e.target.value)} data-testid={`${prefix}-net-input`} /></div>
      )}

      <div className="text-sm p-3 rounded-lg border border-teal-100" style={{ background: "#EAF3F3" }}>
        {name} netto: <b className="tabular-nums" data-testid={`${prefix}-net`}>{money(p.net)}</b>
      </div>

      {mode === "lordo" && (
        <div className="pt-1 space-y-2">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Visibilità nel PDF</div>
          <Flag checked={!!form[`${prefix}_only_net`]} onChange={(v) => upd(`${prefix}_only_net`, v)} label="Mostra solo netto" testid={`${prefix}-only-net`} />
          {!form[`${prefix}_only_net`] && (
            <div className="flex gap-4 flex-wrap">
              <Flag checked={form[`${prefix}_show_gross`] !== false} onChange={(v) => upd(`${prefix}_show_gross`, v)} label="Lordo" testid={`${prefix}-show-gross`} />
              <Flag checked={form[`${prefix}_show_net`] !== false} onChange={(v) => upd(`${prefix}_show_net`, v)} label="Netto" testid={`${prefix}-show-net`} />
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

export default function RendicontoDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [r, setR] = useState(null);
  const [autoCats, setAutoCats] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [meta, setMeta] = useState({ owner: "", apartment: "", property: "" });
  const [confirmSave, setConfirmSave] = useState(false);
  const [changeLog, setChangeLog] = useState(null);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  const load = async () => {
    const { data } = await http.get(`/owner-reports/${id}`);
    setR(data);
    setAutoCats(data.computed?.cost_categories || null);
    setMeta({ owner: data.owner_name || "", apartment: data.apartment_name || "", property: "" });
    if (data.property_id) { try { const p = await http.get(`/properties/${data.property_id}`); setMeta((m) => ({ ...m, property: p.data.name })); } catch {} }
  };
  useEffect(() => { load(); }, [id]);

  // UNICO motore di calcolo: anteprima live dal backend (compute_report), debounce 250ms
  const [live, setLive] = useState(null);
  useEffect(() => {
    if (!r) return;
    if (!live && r.computed) setLive(r.computed);
    const t = setTimeout(async () => {
      try { const { data } = await http.post(`/owner-reports/${id}/preview`, buildPayload()); setLive(data.computed); }
      catch (e) { console.error(e); }
    }, 250);
    return () => clearTimeout(t);
  }, [r]); // eslint-disable-line

  if (!r || !live) return <div className="text-slate-500">Caricamento...</div>;

  const upd = (k, v) => setR({ ...r, [k]: v });
  const ov = (key) => (r.cost_overrides || {})[key] || {};
  const setOv = (key, patch) => upd("cost_overrides", { ...(r.cost_overrides || {}), [key]: { ...ov(key), ...patch } });
  const resetOv = (key) => { const co = { ...(r.cost_overrides || {}) }; delete co[key]; upd("cost_overrides", co); };
  const updCustom = (i, k, v) => { const cc = [...(r.custom_costs || [])]; cc[i] = { ...cc[i], [k]: v }; setR({ ...r, custom_costs: cc }); };
  const addCustom = () => setR({ ...r, custom_costs: [...(r.custom_costs || []), { name: "", amount: 0, considera: true, show_pdf: true }] });
  const rmCustom = (i) => { const cc = [...(r.custom_costs || [])]; cc.splice(i, 1); setR({ ...r, custom_costs: cc }); };

  const buildPayload = () => {
    const payload = { ...r };
    ["booking_net_input","booking_gross","booking_commissions","booking_withholding","booking_other",
     "airbnb_net_input","airbnb_gross","airbnb_commissions","airbnb_withholding","airbnb_other",
     "direct_gross","direct_deductions","other_income","management_percent","management_custom_amount"]
      .forEach((k) => { payload[k] = Number(payload[k] || 0); });
    payload.management_base = normalizeBase(payload.management_base);
    payload.custom_costs = (payload.custom_costs || []).map((c) => ({ name: c.name || "Costo", amount: Number(c.amount || 0), considera: c.considera !== false, show_pdf: c.show_pdf !== false }));
    const co = {};
    for (const [k, v] of Object.entries(payload.cost_overrides || {})) {
      const clean = { considera: v.considera !== false, show_pdf: v.show_pdf !== false };
      if (v.amount != null && v.amount !== "") clean.amount = Number(v.amount);
      if (v.count != null && v.count !== "") clean.count = Number(v.count);
      co[k] = clean;
    }
    payload.cost_overrides = co;
    delete payload.computed; delete payload.id; delete payload.created_at; delete payload.updated_at;
    delete payload.costs_snapshot; delete payload.sent_at; delete payload.paid_at; delete payload.costs_live;
    delete payload.owner_name; delete payload.apartment_name; delete payload.property_id; delete payload.finance; delete payload.finance_enabled;
    delete payload.history; delete payload.linked_costs; delete payload.import_source;
    payload.settlement = payload.settlement || {};
    return payload;
  };

  const save = async (force = false) => {
    const finalized = r.status !== "bozza";
    const savedNet = r.computed?.owner_result;
    if (finalized && !force && Math.abs((live?.owner_result ?? 0) - (savedNet ?? 0)) >= 0.005) { setConfirmSave(true); return null; }
    setConfirmSave(false);
    setSaving(true);
    try {
      const { data } = await http.put(`/owner-reports/${id}`, buildPayload());
      setR(data); setAutoCats(data.computed?.cost_categories || null); setChangeLog(null);
      toast.success("Rendiconto salvato");
      return data;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return null; }
    finally { setSaving(false); }
  };
  const loadHistory = async () => { const { data } = await http.get(`/owner-reports/${id}/history`); setChangeLog(data.history); };

  // Il PDF viene richiesto via XHR autenticato (cookie di sessione) e mostrato come blob:
  // la navigazione diretta all'endpoint in una nuova scheda perde il cookie → "Non autenticato".
  const openPdf = async () => {
    setPdfLoading(true);
    try {
      const resp = await http.get(`/owner-reports/${id}/pdf`, { responseType: "blob" });
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
      setPdfUrl(URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" })));
      setPdfOpen(true);
    } catch (e) { toast.error("Errore generazione PDF"); }
    finally { setPdfLoading(false); }
  };
  const pdfName = `Rendiconto_${mesi[r.month]}_${r.year}.pdf`;

  const openPreview = async () => {
    try {
      const { data } = await http.get("/owner-reports", { params: { year: r.year, apartment_id: r.apartment_id } });
      setHistory(data.filter((x) => x.month <= r.month)
        .map((x) => ({ month: x.month, year: x.year, ricavi: x.computed.total_net, gestione: x.computed.management_fee, costi: x.computed.total_costs, netto: x.computed.owner_result })));
    } catch {}
    setPreviewOpen(true);
  };

  const dup = async () => { const { data } = await http.post(`/owner-reports/${id}/duplicate`); toast.success("Duplicato nel mese successivo"); nav(`/rendiconti/${data.id}`); };
  const refreshCosts = async () => {
    if (!window.confirm("Aggiornare i costi collegati con i valori attuali del database?")) return;
    const { data } = await http.post(`/owner-reports/${id}/refresh-costs`);
    setR(data); setAutoCats(data.computed?.cost_categories || null); toast.success("Costi aggiornati dal database");
  };

  const c = live;
  const locked = !r.costs_live;

  return (
    <div className="space-y-6" data-testid="rendiconto-detail">
      <Link to="/rendiconti" className="text-sm text-slate-500 hover:text-teal-700 inline-flex items-center gap-1"><ChevronLeft className="h-4 w-4" /> Torna ai rendiconti</Link>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{meta.owner}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold" style={{ color: PETROL }}>Rendiconto {mesi[r.month]} {r.year}</h1>
          <div className="text-sm text-slate-500 mt-1">{meta.apartment}{meta.property ? ` · ${meta.property}` : ""}</div>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <Select value={r.status} onValueChange={(v) => upd("status", v)}>
            <SelectTrigger className="w-[140px]" data-testid="rep-status"><SelectValue /></SelectTrigger>
            <SelectContent>{["bozza","controllato","inviato","pagato"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={openPreview} variant="outline" className="rounded-xl min-h-[44px]" data-testid="preview-btn"><Eye className="h-4 w-4 mr-1" /> Anteprima proprietario</Button>
          <Button onClick={openPdf} disabled={pdfLoading} variant="outline" className="rounded-xl min-h-[44px]" data-testid="pdf-btn"><FileText className="h-4 w-4 mr-1" /> {pdfLoading ? "Genero..." : "PDF"}</Button>
          <Button onClick={dup} variant="outline" className="rounded-xl min-h-[44px]"><Copy className="h-4 w-4 mr-1" /> Duplica</Button>
          <Button onClick={() => save(false)} disabled={saving} className="rounded-xl text-white min-h-[44px]" style={{ background: PETROL }} data-testid="save-report-btn">{saving ? "Salvo..." : "Salva"}</Button>
        </div>
      </div>

      <div className="text-xs text-slate-500 flex flex-wrap gap-4 items-center">
        {r.created_at && <span>Creato: {new Date(r.created_at).toLocaleDateString("it-IT")}</span>}
        {r.updated_at && <span>Ultima modifica: {new Date(r.updated_at).toLocaleString("it-IT")}</span>}
        {r.sent_at && <span>Inviato: {new Date(r.sent_at).toLocaleDateString("it-IT")}</span>}
        {r.paid_at && <span>Pagato: {new Date(r.paid_at).toLocaleDateString("it-IT")}</span>}
        <button type="button" onClick={() => (changeLog ? setChangeLog(null) : loadHistory())} className="underline text-[#185659]" data-testid="history-btn">{changeLog ? "Nascondi cronologia" : "Vedi cronologia"}</button>
      </div>
      {changeLog && (
        <Card className="p-4 rounded-2xl text-xs space-y-1" data-testid="history-card">
          {changeLog.length === 0 && <div className="text-slate-400">Nessuna modifica economica registrata.</div>}
          {changeLog.map((h, i) => (
            <div key={i} className="flex flex-wrap gap-x-3 border-b border-slate-100 py-1">
              <span className="text-slate-500">{new Date(h.at).toLocaleString("it-IT")}</span><span className="font-semibold capitalize">{h.origin}</span>
              {h.note && <span className="text-slate-500">{h.note}</span>}
              <span>Netto {money(h.before?.owner_result)} → <b>{money(h.after?.owner_result)}</b></span>
              <span className="text-slate-400">{h.user}</span>
            </div>
          ))}
        </Card>
      )}

      {/* RIEPILOGO CALCOLO — sempre visibile, live */}
      <Card className="p-5 rounded-2xl text-white sticky top-2 z-20 shadow-lg" style={{ background: PETROL }} data-testid="riepilogo-calcolo">
        <div className="flex justify-between items-center mb-2"><div className="text-xs uppercase tracking-widest text-teal-100">Riepilogo calcolo (live)</div>
          {Math.abs((c.owner_result ?? 0) - (r.computed?.owner_result ?? 0)) >= 0.005 && <span className="text-xs bg-amber-300 text-amber-950 font-semibold px-2 py-0.5 rounded-full" data-testid="net-delta">Netto {money(r.computed?.owner_result)} → {money(c.owner_result)} · non salvato</span>}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div><div className="text-teal-100 text-xs">Totale ricavi netti</div><div className="font-display text-xl font-bold tabular-nums" data-testid="total-net">{money(c.total_net)}</div></div>
          <div><div className="text-teal-100 text-xs">Gestione PuraVida Home</div><div className="font-display text-xl font-bold tabular-nums" data-testid="mgmt-fee">- {money(c.management_fee)}</div></div>
          <div><div className="text-teal-100 text-xs">Costi proprietario</div><div className="font-display text-xl font-bold tabular-nums" data-testid="total-costs">- {money(c.total_costs)}</div></div>
          <div className="rounded-xl bg-white/10 p-2"><div className="text-teal-100 text-xs">Netto proprietario</div><div className="font-display text-2xl font-extrabold tabular-nums" data-testid="owner-result">{money(c.owner_result)}</div></div>
        </div>
        {r.owner_result_override != null && r.owner_result_override !== "" && (
          <div className="text-xs mt-2 p-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800" data-testid="net-override-badge">
            Netto storico fisso a <b>{money(r.owner_result_override)}</b>. Alla prima modifica economica verrà convertito in una "Rettifica netto storico" (netto identico), così le modifiche successive ai costi muoveranno il netto.
          </div>
        )}
      </Card>

      <Dialog open={confirmSave} onOpenChange={setConfirmSave}>
        <DialogContent className="sm:max-w-md" data-testid="confirm-save-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl" style={{ color: PETROL }}>Rendiconto già finalizzato</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-700">Questo rendiconto è già finalizzato ({r.status}). La modifica cambierà il netto proprietario da <b>{money(r.computed?.owner_result)}</b> a <b>{money(c.owner_result)}</b>. Lo stato resterà finalizzato e Dashboard/Analisi economica si aggiorneranno.</p>
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setConfirmSave(false)} data-testid="confirm-save-cancel">Annulla</Button><Button style={{ background: PETROL }} onClick={() => save(true)} data-testid="confirm-save-ok">Conferma modifica</Button></div>
        </DialogContent>
      </Dialog>

      <ReportSettlement report={r} computed={c} onReport={(d) => { setR({ ...r, finance: d.finance, finance_enabled: d.finance_enabled }); }}
        settlement={r.settlement} setSettlement={(s) => upd("settlement", s)} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <PlatformCard prefix="booking" name="Booking.com" form={r} upd={upd} live={c} />
        <PlatformCard prefix="airbnb" name="Airbnb" form={r} upd={upd} live={c} />
      </div>

      <Card className="p-5 rounded-2xl space-y-3">
        <div className="font-display text-lg font-bold" style={{ color: PETROL }}>Prenotazioni dirette & altri incassi</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Incasso diretto (€)</Label><Input type="number" step="0.01" value={r.direct_gross ?? 0} onChange={(e) => upd("direct_gross", e.target.value)} data-testid="direct-gross" /></div>
          <div><Label>Costi/trattenute dirette (€)</Label><Input type="number" step="0.01" value={r.direct_deductions ?? 0} onChange={(e) => upd("direct_deductions", e.target.value)} data-testid="direct-ded-amount" /></div>
        </div>
        <div className="flex gap-4 flex-wrap">
          <Flag checked={r.direct_deductions_considera !== false} onChange={(v) => upd("direct_deductions_considera", v)} label="Considera trattenute nel calcolo" testid="direct-ded-considera" />
          <Flag checked={r.direct_deductions_show !== false} onChange={(v) => upd("direct_deductions_show", v)} label="Mostra trattenute nel PDF" testid="direct-ded-show" />
          <Flag checked={r.direct_show_gross !== false} onChange={(v) => upd("direct_show_gross", v)} label="Mostra lordo PDF" testid="direct-show-gross" />
          <Flag checked={r.direct_show_net !== false} onChange={(v) => upd("direct_show_net", v)} label="Mostra netto PDF" testid="direct-show-net" />
        </div>
        <div className="text-sm p-3 rounded-lg border border-teal-100" style={{ background: "#EAF3F3" }}>Dirette nette: <b className="tabular-nums">{money(c.direct_net)}</b></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
          <div><Label>Altri incassi (€)</Label><Input type="number" step="0.01" value={r.other_income ?? 0} onChange={(e) => upd("other_income", e.target.value)} data-testid="other-income-amount" /></div>
          <div className="flex gap-4 flex-wrap">
            <Flag checked={r.other_income_considera !== false} onChange={(v) => upd("other_income_considera", v)} label="Considera nel calcolo" testid="other-income-considera" />
            <Flag checked={r.other_income_show !== false} onChange={(v) => upd("other_income_show", v)} label="Mostra nel PDF" testid="other-income-show" />
          </div>
        </div>
      </Card>

      <Card className="p-5 rounded-2xl space-y-4">
        <div className="font-display text-lg font-bold" style={{ color: PETROL }}>Compenso gestione PuraVida Home</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div><Label>Percentuale (%)</Label><Input type="number" step="0.01" value={r.management_percent ?? 15} onChange={(e) => upd("management_percent", e.target.value)} data-testid="mgmt-percent" /></div>
          <div><Label>Base di calcolo</Label>
            <Select value={normalizeBase(r.management_base)} onValueChange={(v) => upd("management_base", v)}>
              <SelectTrigger data-testid="mgmt-base"><SelectValue /></SelectTrigger>
              <SelectContent>{BASE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent></Select>
          </div>
          {normalizeBase(r.management_base) === "custom" && (
            <div><Label>Base personalizzata (€)</Label><Input type="number" step="0.01" value={r.management_custom_amount ?? 0} onChange={(e) => upd("management_custom_amount", e.target.value)} data-testid="mgmt-custom" /></div>
          )}
        </div>
        <div className="text-sm p-3 rounded-lg border" style={{ background: "rgba(24,86,89,0.07)" }}>
          Compenso gestione: <b className="tabular-nums">{money(c.management_fee)}</b> ({c.management_percent}% × {money(c.management_base_value)}) · senza IVA
        </div>
        {r.management_fee_override != null && r.management_fee_override !== "" && (
          <div className="text-xs p-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800" data-testid="fee-override-badge">
            Valore storico/manuale: il compenso è fissato a <b>{money(r.management_fee_override)}</b> e non ricalcolato da percentuale.
          </div>
        )}
      </Card>

      <Card className="p-5 rounded-2xl space-y-3" data-testid="costs-card">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="font-display text-lg font-bold" style={{ color: PETROL }}>Costi a carico proprietario (collegati automaticamente)</div>
          <div className="flex items-center gap-2">
            {locked && <Badge variant="outline" className="border-blue-300 text-blue-800"><Lock className="h-3 w-3 mr-1" /> Consolidati</Badge>}
            {locked
              ? <Button size="sm" variant="outline" onClick={refreshCosts} data-testid="refresh-costs-btn"><RefreshCw className="h-3 w-3 mr-1" /> Aggiorna costi</Button>
              : <Badge variant="outline" className="border-amber-300 text-amber-800">Live da database</Badge>}
          </div>
        </div>
        <p className="text-xs text-slate-500">Valori automatici da pulizie, lavanderia, consumabili, manutenzioni e uscite di {mesi[r.month]} {r.year}. Puoi fare override del valore finale senza modificare i registri originali.</p>
        <Flag checked={r.show_cleaning_count !== false} onChange={(v) => upd("show_cleaning_count", v)} label="Mostra numero interventi pulizie nel PDF" testid="show-cleaning-count" />
        <div className="space-y-3">
          {COST_CATEGORIES.map(({ key, label, countLabel }) => {
            const cat = c.cost_categories[key];
            const o = ov(key);
            return (
              <div key={key} className="rounded-xl border border-slate-200 p-3 space-y-2" data-testid={`cost-cat-${key}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="font-semibold text-sm">{label}</div>
                  <div className="text-xs text-slate-500">
                    Automatico: {cat.auto_count > 0 && `${cat.auto_count} ${countLabel || "voci"} · `}<b className="tabular-nums">{money(cat.auto_amount)}</b>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  {countLabel && (
                    <div className="flex items-center gap-2">
                      <Label className="text-xs whitespace-nowrap">N° {countLabel}</Label>
                      <Input type="number" min="0" className="w-20" value={o.count ?? ""} placeholder={String(cat.auto_count)}
                        onChange={(e) => setOv(key, { count: e.target.value === "" ? null : e.target.value })} data-testid={`ov-${key}-count`} />
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Label className="text-xs whitespace-nowrap">Valore finale (€)</Label>
                    <Input type="number" step="0.01" className="w-28 text-right" value={o.amount ?? ""} placeholder={String(cat.auto_amount)}
                      onChange={(e) => setOv(key, { amount: e.target.value === "" ? null : e.target.value })} data-testid={`ov-${key}-amount`} />
                  </div>
                  <div className="tabular-nums font-semibold text-sm" data-testid={`eff-${key}`}>{money(cat.amount)}</div>
                  {cat.overridden && (
                    <button type="button" onClick={() => resetOv(key)} data-testid={`reset-${key}`}
                      className="text-xs font-semibold text-[#185659] underline">Ripristina valore automatico</button>
                  )}
                </div>
                <div className="flex gap-4">
                  <Flag checked={o.considera !== false} onChange={(v) => setOv(key, { considera: v })} label="Addebita al proprietario" testid={`ov-${key}-considera`} />
                  <Flag checked={o.show_pdf !== false} onChange={(v) => setOv(key, { show_pdf: v })} label="Mostra nel PDF" testid={`ov-${key}-show`} />
                </div>
              </div>
            );
          })}
        </div>
        <div className="pt-3 border-t border-slate-200">
          <div className="flex items-center justify-between mb-2"><div className="text-sm font-semibold text-slate-700">Costi straordinari manuali</div><Button type="button" size="sm" variant="outline" onClick={addCustom} data-testid="add-custom-cost"><Plus className="h-3 w-3 mr-1" /> Aggiungi</Button></div>
          <div className="space-y-2">
            {(r.custom_costs || []).map((cc, i) => (
              <div key={i} className="flex gap-2 items-center flex-wrap">
                <Input placeholder="Nome" value={cc.name} onChange={(e) => updCustom(i, "name", e.target.value)} className="flex-1 min-w-[120px]" />
                <Input type="number" step="0.01" value={cc.amount} onChange={(e) => updCustom(i, "amount", e.target.value)} className="w-28" />
                <Flag checked={cc.considera !== false} onChange={(v) => updCustom(i, "considera", v)} label="Addebita" testid={`custom-${i}-considera`} />
                <Flag checked={cc.show_pdf !== false} onChange={(v) => updCustom(i, "show_pdf", v)} label="PDF" testid={`custom-${i}-show`} />
                <button type="button" onClick={() => rmCustom(i)} className="p-2 text-rose-500"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        </div>
        <div><Label>Note</Label><Textarea rows={2} value={r.notes || ""} onChange={(e) => upd("notes", e.target.value)} /></div>
      </Card>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="preview-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl" style={{ color: PETROL }}>Anteprima proprietario</DialogTitle></DialogHeader>
          <ReportPreview computed={c} meta={{ owner: meta.owner, apartment: meta.apartment, property: meta.property, month: r.month, year: r.year, notes: r.notes }} history={history} />
          <p className="text-xs text-slate-500 text-center">Contenuti identici al PDF finale. Il grafico dell'andamento è incluso nel PDF.</p>
        </DialogContent>
      </Dialog>

      <Dialog open={pdfOpen} onOpenChange={setPdfOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="pdf-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl" style={{ color: PETROL }}>Rendiconto PDF</DialogTitle></DialogHeader>
          {pdfUrl && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2">
                <Button asChild className="rounded-xl text-white min-h-[44px]" style={{ background: PETROL }}>
                  <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="pdf-open-link">Apri il PDF</a>
                </Button>
                <Button asChild variant="outline" className="rounded-xl min-h-[44px]">
                  <a href={pdfUrl} download={pdfName} data-testid="pdf-download-link">Salva / Condividi</a>
                </Button>
              </div>
              <p className="text-xs text-slate-500">Su iPad/iPhone tocca "Apri il PDF" per visualizzarlo, poi usa il pulsante Condividi di iOS per salvarlo o inviarlo al proprietario.</p>
              <iframe src={pdfUrl} title="Rendiconto PDF" data-testid="pdf-frame" className="w-full h-[60vh] rounded-xl border border-slate-200" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
